import { useCallback } from "react";
import { useAuth, useUser } from "@clerk/clerk-react";
import { ClerkUserData } from "../types/clerk";
import { resetDashboardDateWindowForAuthSession } from "../utils/dashboardDateWindowStorage";

/**
 * Custom hook for Clerk authentication
 * Provides simplified access to authentication state and user data
 */
export function useClerkAuth() {
  const { isLoaded, isSignedIn, signOut: clerkSignOut } = useAuth();
  const { user } = useUser();

  const signOut = useCallback(
    (...args: Parameters<typeof clerkSignOut>) => {
      resetDashboardDateWindowForAuthSession();
      return clerkSignOut(...args);
    },
    [clerkSignOut]
  );

  // Transform Clerk user object to our ClerkUserData type
  const userData: ClerkUserData | null = user
    ? {
        id: user.id,
        firstName: user.firstName,
        lastName: user.lastName,
        emailAddresses: user.emailAddresses.map((email) => ({
          emailAddress: email.emailAddress,
        })),
        primaryEmailAddress: user.primaryEmailAddress?.emailAddress ?? null,
        phoneNumbers: user.phoneNumbers?.map((phone) => ({
          phoneNumber: phone.phoneNumber,
        })),
        imageUrl: user.imageUrl,
        createdAt: user.createdAt ? user.createdAt.getTime() : Date.now(),
        updatedAt: user.updatedAt ? user.updatedAt.getTime() : Date.now(),
      }
    : null;

  return {
    isLoaded,
    isSignedIn: isSignedIn || false,
    user: userData,
    signOut,
  };
}

